import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionDivider } from './shared';

function MetricItem({ icon, label, value }) {
  if (!value) return null;
  return (
    <div style={s.metItem}>
      <span style={s.metIcon}>{icon}</span>
      <div style={s.metInfo}>
        <span style={s.metLabel}>{label}</span>
        <span style={s.metValue}>{value}</span>
      </div>
    </div>
  );
}

export default function MetricsCard({ data, applications }) {
  const { t } = useTranslation();
  const { funding, community, testnet, kpis } = data;
  const { metrics } = applications;

  return (
    <Card title={t('metricsCard.title')} icon="📊">
      {funding.total && (
        <>
          <div style={s.fundHero}>
            <span style={s.fundLabel}>{t('metricsCard.totalRaised')}</span>
            <span style={s.fundValue}>{funding.total}</span>
          </div>
          {funding.investors && (
            <div style={s.infoBox}>
              <span style={s.infoLabel}>{t('metricsCard.leadInvestors')}</span>
              <span style={s.infoValue}>{funding.investors}</span>
            </div>
          )}
          {funding.rounds && (
            <div style={s.infoBox}>
              <span style={s.infoLabel}>{t('metricsCard.fundingRounds')}</span>
              <span style={s.infoValue}>{funding.rounds}</span>
            </div>
          )}
        </>
      )}

      {Object.values(community).some(Boolean) && (
        <>
          <SectionDivider label={t('metricsCard.community')} />
          <div style={s.commGrid}>
            <MetricItem icon="𝕏"  label="Twitter"  value={community.twitter} />
            <MetricItem icon="💬" label="Discord"  value={community.discord} />
            <MetricItem icon="✈️" label="Telegram" value={community.telegram} />
            <MetricItem icon="🐙" label="GitHub"   value={community.github} />
          </div>
        </>
      )}

      {Object.values(metrics).some(Boolean) && (
        <>
          <SectionDivider label="On-chain" />
          <div style={s.commGrid}>
            <MetricItem icon="🔒" label="TVL"         value={metrics['tvl']} />
            <MetricItem icon="👤" label="Daily Users" value={metrics['daily_active']} />
            <MetricItem icon="⚡" label={t('metricsCard.transactions')} value={metrics['total_transactions']} />
            <MetricItem icon="👛" label={t('metricsCard.wallets')} value={metrics['unique_wallets']} />
          </div>
        </>
      )}

      {Object.values(testnet).some(Boolean) && (
        <>
          <SectionDivider label="Testnet / Beta" />
          <div style={s.commGrid}>
            <MetricItem icon="🧪" label="Testnet Users" value={testnet.users} />
            <MetricItem icon="📦" label={t('metricsCard.transactions')} value={testnet.txns} />
          </div>
          {testnet.beta && <p style={s.beta}>{testnet.beta}</p>}
        </>
      )}

      {kpis.length > 0 && (
        <>
          <SectionDivider label={t('metricsCard.otherKpis')} />
          <ul style={s.kpiList}>
            {kpis.map((k, i) => <li key={i} style={s.kpiItem}>{k}</li>)}
          </ul>
        </>
      )}
    </Card>
  );
}

const s = {
  fundHero: {
    background: 'linear-gradient(135deg, rgba(5,150,105,0.1), rgba(8,145,178,0.07))',
    border: '1px solid rgba(5,150,105,0.25)',
    borderRadius: 10, padding: '12px 16px', marginBottom: 10,
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  fundLabel: { fontSize: 10, color: '#059669', fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' },
  fundValue: { fontSize: 22, fontWeight: 800, color: 'var(--text)', wordBreak: 'break-word', overflowWrap: 'break-word' },
  infoBox: { background: 'var(--surface2)', borderRadius: 8, padding: '9px 12px', marginBottom: 6, border: '1px solid var(--border)' },
  infoLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.7 },
  infoValue: { fontSize: 12, color: 'var(--text-dim)', wordBreak: 'break-word', overflowWrap: 'break-word' },
  commGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4, minWidth: 0 },
  metItem: {
    display: 'flex', alignItems: 'center', gap: 9,
    background: 'var(--surface2)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 12px', minWidth: 0,
  },
  metIcon: { fontSize: 16, flexShrink: 0, lineHeight: 1 },
  metInfo: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  metLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 },
  metValue: { fontSize: 12, color: 'var(--text)', fontWeight: 600, wordBreak: 'break-word', overflowWrap: 'break-word' },
  beta: { fontSize: 12, color: 'var(--text-dim)', margin: '6px 0 0' },
  kpiList: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 },
  kpiItem: { fontSize: 12, color: 'var(--text-muted)', paddingLeft: 12, position: 'relative', lineHeight: 1.5 },
};
